// SPDX-License-Identifier: LicenseRef-Proprietary

import { useMemo } from 'react';
import type { VaccineOutputs, Scenario, VaccineInputs } from '../lib/models';

interface UseVaccineTabsParams {
  model: VaccineOutputs | null;
  inputs: VaccineInputs;
  fiscalData: Array<{ year: number; programmeCost: number; healthcareSavings: number }>;
  scenarios: Scenario[];
}

/**
 * Hook to generate tab data for vaccine dashboard
 * Returns data objects instead of JSX for better separation of concerns
 * JSX composition happens in TabPanelConnected component
 */
export function useVaccineTabs({
  model,
  inputs,
  fiscalData,
  scenarios,
}: UseVaccineTabsParams) {
  const compareData = useMemo(() => {
    return scenarios.map((s) => ({
      name: s.name,
      Net: Math.round(s.outputs.netSavings),
      Savings: Math.round(s.outputs.totalSavings),
      Costs: Math.round(s.outputs.vaccineCosts),
      QALYs: Number(s.outputs.qalyGained.toFixed(3)),
    }));
  }, [scenarios]);

  return useMemo(() => {
    if (!model) {
      return {
        timelineData: [],
        breakdownData: {
          totalSavings: 0,
          vaccineCosts: 0,
          pieData: [],
        },
        costBreakdownData: {
          directMedicalCost: 0,
          programmeDeliveryCost: 0,
          productivityCost: 0,
          adverseEventCost: 0,
        },
        waterfallData: {
          treatmentSavings: 0,
          productivitySavings: 0,
        },
        compareData,
        fiscalData,
      };
    }

    return {
      timelineData: model.series,
      breakdownData: {
        totalSavings: model.totalSavings,
        vaccineCosts: model.vaccineCosts,
        pieData: model.pieData,
      },
      costBreakdownData: model.costBreakdown,
      waterfallData: {
        treatmentSavings: model.casesCostSaved,
        productivitySavings: model.productivitySaved,
      },
      compareData,
      fiscalData,
    };
  }, [model, compareData, fiscalData]);
}

